import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, computed, flow, observable } from "mobx";
import { routeLocationCashbackClaimPost, routeLocationPaddleIncompleteCheckoutGet } from "@devowl-wp/api-real-commerce";
import { RealCommerceError } from "../exceptions/realCommerceError.js";
import { PaddleTransaction } from "../models/paddleTransaction.js";
import { Product } from "../models/product.js";
import { realCommerceRequest } from "../utils/realCommerceRequest.js";
var CheckoutStep = /*#__PURE__*/ function(CheckoutStep) {
    CheckoutStep[CheckoutStep["Scratch"] = 0] = "Scratch";
    CheckoutStep[CheckoutStep["ProductLoaded"] = 1] = "ProductLoaded";
    CheckoutStep[CheckoutStep["VendorLoaded"] = 2] = "VendorLoaded";
    CheckoutStep[CheckoutStep["AppliedIdentification"] = 3] = "AppliedIdentification";
    CheckoutStep[CheckoutStep["PaymentFrameLoaded"] = 4] = "PaymentFrameLoaded";
    CheckoutStep[CheckoutStep["AwaitingPayment"] = 5] = "AwaitingPayment";
    CheckoutStep[CheckoutStep["Paid"] = 6] = "Paid";
    /**
     * When the transaction got retried x times to complete but did not yet
     * finish after the x.th retry, show confirmation.
     */ CheckoutStep[CheckoutStep["NotYetProcessedButShowConfirmation"] = 7] = "NotYetProcessedButShowConfirmation";
    return CheckoutStep;
}(CheckoutStep || {});
class CheckoutV1Store {
    get loading() {
        return this.step < 2 || this.busyRecover || this.busyCashbackClaim;
    }
    constructor(rootStore){
        this.busyRecover = false;
        this.busyCashbackClaim = false;
        this.step = 0;
        this.hasCheckoutFormInteraction = false;
        this.startTransaction = flow(function*(params, recovered) {
            this.setStep(0);
            this.setHasCheckoutFormInteraction(false);
            this.product = yield Product.loadProduct(params, this);
            // Start product transaction (currently only Paddle is supported)
            const { name } = this.product.data.productPlan.product.ecommerceSystem;
            switch(name.toLowerCase()){
                case "paddle":
                    this.transaction = new PaddleTransaction(this.product, recovered);
                    break;
                default:
                    throw new Error(`${name} transactions not yet implemented.`);
            }
            this.setStep(1);
            // Load external vendor files
            yield this.transaction.loadVendorScripts();
            this.setStep(2);
        });
        /**
     * Recover incomplete checkout from a given UUID and automatically start the transaction.
     *
     * @param uuid
     */ this.recoverIncompleteCheckout = flow(function*(uuid) {
            this.busyRecover = true;
            try {
                const result = yield realCommerceRequest({
                    location: routeLocationPaddleIncompleteCheckoutGet,
                    params: {
                        uuid
                    }
                });
                // Start transaction with the known product
                const { checkout: { product: { id: productId }, productPlan: { id: productPlanId }, currency } } = result;
                this.startTransaction({
                    productId,
                    productPlanId,
                    currency
                }, result);
            } catch (e) {
                throw new RealCommerceError(e);
            } finally{
                this.busyRecover = false;
            }
        });
        /**
     * Claim cashback.
     *
     * @param uuid
     */ this.claimCashback = flow(function*(cashbackToken) {
            this.busyCashbackClaim = true;
            try {
                const { checkout: { cashbackPromotion } } = yield realCommerceRequest({
                    location: routeLocationCashbackClaimPost,
                    request: {
                        checkout: {
                            cashbackToken
                        }
                    }
                });
                this.claimedCashback = cashbackPromotion;
            } catch (e) {
                this.claimedCashback = new RealCommerceError(e);
            } finally{
                this.busyCashbackClaim = false;
            }
        });
        this.rootStore = rootStore;
    }
    setStep(step) {
        this.step = step;
    }
    setHasCheckoutFormInteraction(value) {
        this.hasCheckoutFormInteraction = value;
    }
    markCheckoutFormInteraction() {
        if (!this.hasCheckoutFormInteraction) {
            this.hasCheckoutFormInteraction = true;
        }
    }
}
_ts_decorate([
    observable
], CheckoutV1Store.prototype, "busyRecover", void 0);
_ts_decorate([
    observable
], CheckoutV1Store.prototype, "busyCashbackClaim", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Number)
], CheckoutV1Store.prototype, "step", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Product === "undefined" ? Object : Product)
], CheckoutV1Store.prototype, "product", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Transaction === "undefined" ? Object : Transaction)
], CheckoutV1Store.prototype, "transaction", void 0);
_ts_decorate([
    observable
], CheckoutV1Store.prototype, "hasCheckoutFormInteraction", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], CheckoutV1Store.prototype, "claimedCashback", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], CheckoutV1Store.prototype, "loading", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Number
    ]),
    _ts_metadata("design:returntype", void 0)
], CheckoutV1Store.prototype, "setStep", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Boolean
    ]),
    _ts_metadata("design:returntype", void 0)
], CheckoutV1Store.prototype, "setHasCheckoutFormInteraction", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], CheckoutV1Store.prototype, "markCheckoutFormInteraction", null);
export { CheckoutV1Store, CheckoutStep };
